import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Lock,
  LogOut,
  Mail,
  Phone,
  RefreshCw,
  Search,
  MessageSquare,
  Calculator,
  Loader2,
  ChevronDown,
  ArrowRight,
  Bell,
  Check,
  Undo2,
  Download,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const STORAGE_KEY = "ced_admin_token";
const POLL_MS = 30000;

export default function Admin() {
  const [token, setToken] = useState(
    () => sessionStorage.getItem(STORAGE_KEY) || ""
  );
  const [draftToken, setDraftToken] = useState("");
  const [authing, setAuthing] = useState(false);

  const [tab, setTab] = useState("contact");
  const [statusFilter, setStatusFilter] = useState("outstanding"); // outstanding | all | handled
  const [contacts, setContacts] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [summary, setSummary] = useState({
    contact_count: 0,
    quote_count: 0,
    contact_outstanding: 0,
    quote_outstanding: 0,
  });
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);
  // Realtime new-submission tracking
  const [newCounts, setNewCounts] = useState({ contact: 0, quote: 0 });
  const seenRef = useRef({ contact: 0, quote: 0 });

  const [exporting, setExporting] = useState(false);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/admin/export/${tab}`, {
        headers: { "X-Admin-Token": token },
        responseType: "blob",
      });
      const disposition = res.headers["content-disposition"] || "";
      const match = disposition.match(/filename="?([^"]+)"?/);
      const filename =
        match?.[1] || `cutting-edge-${tab}-${new Date().toISOString().slice(0, 10)}.csv`;
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${tab === "contact" ? "enquiries" : "quotes"} to CSV`);
    } catch {
      toast.error("Export failed. Try again.");
    } finally {
      setExporting(false);
    }
  };

  const headers = useMemo(
    () => (token ? { "X-Admin-Token": token } : {}),
    [token]
  );

  const fetchAll = async (t = token, opts = { resetSeen: false }) => {
    if (!t) return;
    setLoading(true);
    try {
      const [c, q, s] = await Promise.all([
        axios.get(`${API}/contact`, { headers: { "X-Admin-Token": t } }),
        axios.get(`${API}/quote`, { headers: { "X-Admin-Token": t } }),
        axios.get(`${API}/admin/summary`, { headers: { "X-Admin-Token": t } }),
      ]);
      setContacts(c.data || []);
      setQuotes(q.data || []);
      setSummary(
        s.data || {
          contact_count: 0,
          quote_count: 0,
          contact_outstanding: 0,
          quote_outstanding: 0,
        }
      );
      if (opts.resetSeen) {
        seenRef.current = {
          contact: s.data?.contact_count ?? 0,
          quote: s.data?.quote_count ?? 0,
        };
        setNewCounts({ contact: 0, quote: 0 });
        document.title = "Cutting Edge · Admin";
      }
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Invalid admin token");
        sessionStorage.removeItem(STORAGE_KEY);
        setToken("");
      } else {
        toast.error("Failed to load submissions");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchAll(token, { resetSeen: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Auto-poll for new submissions every 30s
  useEffect(() => {
    if (!token) return undefined;
    let cancelled = false;

    const tick = async () => {
      try {
        const { data } = await axios.get(`${API}/admin/summary`, {
          headers: { "X-Admin-Token": token },
        });
        if (cancelled) return;
        const newContact = Math.max(
          0,
          (data.contact_count ?? 0) - seenRef.current.contact
        );
        const newQuote = Math.max(
          0,
          (data.quote_count ?? 0) - seenRef.current.quote
        );
        setNewCounts({ contact: newContact, quote: newQuote });
        // Update summary live (but don't touch seenRef until "Show new")
        setSummary(data);
        if (newContact + newQuote > 0) {
          // Update document title so the tab itself signals
          document.title = `(${newContact + newQuote}) Cutting Edge · Admin`;
        } else {
          document.title = "Cutting Edge · Admin";
        }
      } catch {
        /* swallow — next tick will retry */
      }
    };

    const id = setInterval(tick, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
      document.title = "Cutting Edge Detail — Pukekohe, NZ";
    };
  }, [token]);

  const showNew = async () => {
    await fetchAll(token, { resetSeen: true });
    toast.success("Loaded the latest submissions");
  };

  const toggleHandled = async (kind, id, current) => {
    setUpdatingId(id);
    try {
      const { data } = await axios.patch(
        `${API}/admin/${kind}/${id}/handled`,
        { handled: !current },
        { headers: { "X-Admin-Token": token } }
      );
      const updater = (rows) =>
        rows.map((r) => (r.id === id ? { ...r, ...data } : r));
      if (kind === "contact") setContacts(updater);
      else setQuotes(updater);
      // Refresh summary counts (handled/outstanding split)
      try {
        const { data: sum } = await axios.get(`${API}/admin/summary`, {
          headers: { "X-Admin-Token": token },
        });
        setSummary(sum);
      } catch {
        /* non-critical */
      }
      toast.success(data.handled ? "Marked as handled" : "Reopened");
    } catch (err) {
      toast.error("Couldn't update. Try again.");
    } finally {
      setUpdatingId(null);
    }
  };

  const handleAuth = async (e) => {
    e.preventDefault();
    if (!draftToken.trim()) return;
    setAuthing(true);
    try {
      await axios.get(`${API}/admin/summary`, {
        headers: { "X-Admin-Token": draftToken },
      });
      sessionStorage.setItem(STORAGE_KEY, draftToken);
      setToken(draftToken);
      setDraftToken("");
      toast.success("Welcome back");
    } catch (err) {
      const code = err?.response?.status;
      if (code === 401) toast.error("Wrong password");
      else toast.error("Couldn't sign in. Try again.");
    } finally {
      setAuthing(false);
    }
  };

  const logout = () => {
    sessionStorage.removeItem(STORAGE_KEY);
    setToken("");
    setContacts([]);
    setQuotes([]);
    setNewCounts({ contact: 0, quote: 0 });
    seenRef.current = { contact: 0, quote: 0 };
    document.title = "Cutting Edge Detail — Pukekohe, NZ";
  };

  const search = (list, keys) => {
    if (!query.trim()) return list;
    const q = query.trim().toLowerCase();
    return list.filter((row) =>
      keys.some((k) =>
        String(row?.[k] ?? "")
          .toLowerCase()
          .includes(q)
      )
    );
  };

  const byStatus = (list) => {
    if (statusFilter === "all") return list;
    if (statusFilter === "handled") return list.filter((r) => r.handled);
    return list.filter((r) => !r.handled);
  };

  const contactsFiltered = byStatus(
    search(contacts, ["name", "email", "phone", "subject", "message"])
  );
  const quotesFiltered = byStatus(
    search(quotes, [
      "name",
      "email",
      "phone",
      "service_tier",
      "vehicle_size",
      "notes",
    ])
  );

  // ---------------- AUTH GATE ----------------
  if (!token) {
    return (
      <div
        data-testid="admin-login"
        className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-6"
      >
        <form
          onSubmit={handleAuth}
          className="w-full max-w-sm border border-[#D4AF37]/30 bg-[#0F0F0F] p-8"
        >
          <div className="w-10 h-10 border border-[#D4AF37] flex items-center justify-center text-[#D4AF37] mb-6">
            <Lock className="w-4 h-4" />
          </div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">
            Cutting Edge · Admin
          </p>
          <h1 className="font-display text-white font-light text-3xl tracking-tight mt-3">
            Sign in
          </h1>
          <p className="text-white/50 text-sm mt-2">
            Enter the admin password to view customer submissions.
          </p>
          <input
            data-testid="admin-token-input"
            type="password"
            value={draftToken}
            onChange={(e) => setDraftToken(e.target.value)}
            className="input-dark w-full px-4 py-3 mt-6 text-sm"
            placeholder="Admin password"
            required
            autoFocus
          />
          <button
            data-testid="admin-login-submit"
            type="submit"
            disabled={authing}
            className="btn-gold w-full px-6 py-3.5 text-xs mt-4 flex items-center justify-center gap-3 disabled:opacity-60"
          >
            {authing ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Verifying
              </>
            ) : (
              <>
                Sign In <ArrowRight className="w-4 h-4" />
              </>
            )}
          </button>
          <a
            href="/"
            className="block text-center text-white/40 text-xs mt-5 hover:text-[#D4AF37] transition"
          >
            ← Back to site
          </a>
        </form>
      </div>
    );
  }

  // ---------------- DASHBOARD ----------------
  return (
    <div
      data-testid="admin-dashboard"
      className="min-h-screen bg-[#0A0A0A] text-white"
    >
      {/* Header */}
      <header className="border-b border-white/8 bg-black/60 backdrop-blur-md sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="block w-8 h-8 border border-[#D4AF37] rotate-45 relative">
              <span className="absolute inset-1 bg-[#D4AF37]/10" />
            </span>
            <div>
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] whitespace-nowrap">
                Cutting Edge
              </p>
              <p className="font-display text-white text-base leading-tight whitespace-nowrap">
                Submissions Portal
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {newCounts.contact + newCounts.quote > 0 && (
              <button
                data-testid="admin-new-badge"
                onClick={showNew}
                className="relative flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-black text-[11px] uppercase tracking-[0.18em] font-semibold animate-pulse hover:animate-none transition"
              >
                <Bell className="w-3.5 h-3.5" />
                {newCounts.contact + newCounts.quote} New · Show
                <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-[#D4AF37] ring-2 ring-[#0A0A0A]" />
              </button>
            )}
            <button
              data-testid="admin-export-csv"
              onClick={exportCsv}
              disabled={exporting}
              className="btn-outline-gold px-4 py-2 text-[11px] flex items-center gap-2 disabled:opacity-60"
              title={`Download all ${tab === "contact" ? "enquiries" : "quotes"} as CSV`}
            >
              {exporting ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Download className="w-3.5 h-3.5" />
              )}
              <span className="hidden sm:inline">Export CSV</span>
            </button>
            <button
              data-testid="admin-refresh"
              onClick={() => fetchAll(token, { resetSeen: true })}
              className="btn-outline-gold px-4 py-2 text-[11px] flex items-center gap-2"
            >
              <RefreshCw
                className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`}
              />
              <span className="hidden sm:inline">Refresh</span>
            </button>
            <button
              data-testid="admin-logout"
              onClick={logout}
              className="text-white/60 hover:text-[#D4AF37] px-3 py-2 text-[11px] uppercase tracking-[0.2em] flex items-center gap-2"
            >
              <LogOut className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 md:px-10 py-10">
        {/* Stat tiles */}
        <div className="grid sm:grid-cols-2 gap-5 mb-10">
          <StatCard
            testid="admin-stat-contact"
            label="Booking enquiries"
            value={summary.contact_count}
            outstanding={summary.contact_outstanding}
            newCount={newCounts.contact}
            icon={MessageSquare}
            active={tab === "contact"}
            onClick={() => setTab("contact")}
          />
          <StatCard
            testid="admin-stat-quote"
            label="Quote requests"
            value={summary.quote_count}
            outstanding={summary.quote_outstanding}
            newCount={newCounts.quote}
            icon={Calculator}
            active={tab === "quote"}
            onClick={() => setTab("quote")}
          />
        </div>

        {/* Status filter pills */}
        <div className="flex flex-wrap items-center gap-2 mb-5">
          {[
            { id: "outstanding", label: "Outstanding" },
            { id: "all", label: "All" },
            { id: "handled", label: "Handled" },
          ].map((p) => (
            <button
              key={p.id}
              data-testid={`admin-filter-${p.id}`}
              onClick={() => setStatusFilter(p.id)}
              className={`px-4 py-2 text-[11px] uppercase tracking-[0.2em] border transition ${
                statusFilter === p.id
                  ? "bg-[#D4AF37] text-black border-[#D4AF37]"
                  : "border-white/10 text-white/60 hover:border-[#D4AF37]/60 hover:text-white"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <Search className="w-4 h-4 text-white/40 absolute left-4 top-1/2 -translate-y-1/2" />
          <input
            data-testid="admin-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={`Search ${tab === "contact" ? "enquiries" : "quotes"}…`}
            className="input-dark w-full pl-11 pr-4 py-3 text-sm"
          />
        </div>

        {/* Lists */}
        {tab === "contact" ? (
          <ContactList
            rows={contactsFiltered}
            expanded={expanded}
            onToggle={setExpanded}
            empty={contacts.length === 0}
            onToggleHandled={(id, current) => toggleHandled("contact", id, current)}
            updatingId={updatingId}
          />
        ) : (
          <QuoteList
            rows={quotesFiltered}
            expanded={expanded}
            onToggle={setExpanded}
            empty={quotes.length === 0}
            onToggleHandled={(id, current) => toggleHandled("quote", id, current)}
            updatingId={updatingId}
          />
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, outstanding, newCount, icon: Icon, active, onClick, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`text-left p-6 border transition-all duration-300 ${
        active
          ? "border-[#D4AF37]/60 bg-[#D4AF37]/5"
          : "border-white/8 hover:border-white/25"
      }`}
    >
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3">
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">
              {label}
            </p>
            {newCount > 0 && (
              <span
                data-testid={`${testid}-new`}
                className="text-[10px] uppercase tracking-[0.18em] bg-[#D4AF37] text-black px-2 py-0.5 font-semibold animate-pulse"
              >
                +{newCount} new
              </span>
            )}
          </div>
          <p className="font-display text-white text-4xl font-light mt-3">
            <span data-testid={`${testid}-outstanding`}>{outstanding ?? value}</span>
            <span className="text-white/30 text-2xl ml-2">/ {value}</span>
          </p>
          <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 mt-2">
            outstanding · total
          </p>
        </div>
        <div
          className={`w-10 h-10 border flex items-center justify-center ${
            active
              ? "border-[#D4AF37] text-[#D4AF37]"
              : "border-white/15 text-white/50"
          }`}
        >
          <Icon className="w-4 h-4" />
        </div>
      </div>
    </button>
  );
}

function dateFmt(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso.slice(0, 19).replace("T", " ");
  return d.toLocaleString("en-NZ", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function preferredFmt(r) {
  if (!r.preferred_date && !r.preferred_slot) return "Flexible";
  const parts = [];
  if (r.preferred_date) {
    const d = new Date(`${r.preferred_date}T00:00:00`);
    parts.push(
      isNaN(d.getTime())
        ? r.preferred_date
        : d.toLocaleDateString("en-NZ", { weekday: "short", day: "numeric", month: "short", year: "numeric" })
    );
  }
  if (r.preferred_slot) parts.push(r.preferred_slot);
  return parts.join(" · ");
}

function RowShell({ children, expanded, onClick, testid }) {
  return (
    <div
      data-testid={testid}
      className={`border transition-all ${
        expanded
          ? "border-[#D4AF37]/50 bg-[#D4AF37]/4"
          : "border-white/8 bg-[#0E0E0E] hover:border-white/20"
      }`}
    >
      <button
        onClick={onClick}
        className="w-full text-left p-5 flex items-center justify-between gap-4"
      >
        {children}
      </button>
    </div>
  );
}

function HandledButton({ row, onToggleHandled, updatingId, testid }) {
  const busy = updatingId === row.id;
  return (
    <button
      data-testid={testid}
      onClick={() => onToggleHandled(row.id, row.handled)}
      disabled={busy}
      className={`px-5 py-2.5 text-[11px] flex items-center gap-2 border transition disabled:opacity-60 ${
        row.handled
          ? "border-white/15 text-white/60 hover:border-[#D4AF37] hover:text-[#D4AF37]"
          : "btn-outline-gold"
      }`}
    >
      {busy ? (
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
      ) : row.handled ? (
        <Undo2 className="w-3.5 h-3.5" />
      ) : (
        <Check className="w-3.5 h-3.5" />
      )}
      {row.handled ? "Reopen" : "Mark as handled"}
    </button>
  );
}

function HandledPill({ row }) {
  if (!row.handled) return null;
  return (
    <span
      data-testid={`handled-pill-${row.id}`}
      className="text-[9px] uppercase tracking-[0.25em] bg-[#D4AF37]/15 text-[#D4AF37] px-2 py-0.5 border border-[#D4AF37]/40"
    >
      Handled
    </span>
  );
}

function ContactList({ rows, expanded, onToggle, empty, onToggleHandled, updatingId }) {
  if (empty) {
    return (
      <EmptyState
        title="No enquiries yet"
        body="Booking enquiries from the contact form will show up here in real time."
      />
    );
  }
  if (rows.length === 0)
    return <EmptyState title="Nothing here" body="Try a different filter or search." />;
  return (
    <div className="space-y-3">
      {rows.map((r) => {
        const open = expanded === `c-${r.id}`;
        return (
          <div key={r.id} className={r.handled ? "opacity-65" : ""}>
            <RowShell
              testid={`contact-row-${r.id}`}
              expanded={open}
              onClick={() => onToggle(open ? null : `c-${r.id}`)}
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-display text-white text-lg leading-tight truncate">
                    {r.name}{" "}
                    <span className="text-white/40 text-sm font-sans">
                      · {r.subject}
                    </span>
                  </p>
                  <HandledPill row={r} />
                </div>
                <p className="text-white/50 text-xs mt-1">{dateFmt(r.created_at)}</p>
              </div>
              <ChevronDown
                className={`w-4 h-4 text-white/50 transition-transform shrink-0 ${
                  open ? "rotate-180 text-[#D4AF37]" : ""
                }`}
              />
            </RowShell>
            {open && (
              <div className="border border-t-0 border-[#D4AF37]/30 bg-[#0D0B05] p-6 grid md:grid-cols-2 gap-5">
                <Field label="Email">
                  <a
                    href={`mailto:${r.email}`}
                    className="link-gold break-all"
                  >
                    {r.email}
                  </a>
                </Field>
                <Field label="Phone">
                  <a href={`tel:${r.phone}`} className="link-gold">
                    {r.phone}
                  </a>
                </Field>
                {r.address && (
                  <Field label="Address" full>
                    {r.address}
                  </Field>
                )}
                <Field label="Message" full>
                  <p className="whitespace-pre-wrap leading-relaxed">
                    {r.message}
                  </p>
                </Field>
                <div className="md:col-span-2 flex flex-wrap gap-3 mt-2">
                  <a
                    href={`mailto:${r.email}?subject=Re: ${encodeURIComponent(
                      r.subject || ""
                    )}`}
                    className="btn-gold px-5 py-2.5 text-[11px] flex items-center gap-2"
                  >
                    <Mail className="w-3.5 h-3.5" /> Reply via Email
                  </a>
                  <a
                    href={`tel:${r.phone}`}
                    className="btn-outline-gold px-5 py-2.5 text-[11px] flex items-center gap-2"
                  >
                    <Phone className="w-3.5 h-3.5" /> Call
                  </a>
                  <HandledButton
                    row={r}
                    onToggleHandled={onToggleHandled}
                    updatingId={updatingId}
                    testid={`contact-handled-${r.id}`}
                  />
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function QuoteList({ rows, expanded, onToggle, empty, onToggleHandled, updatingId }) {
  if (empty) {
    return (
      <EmptyState
        title="No quote requests yet"
        body="Instant Quote submissions will show up here as customers build their quote."
      />
    );
  }
  if (rows.length === 0)
    return <EmptyState title="Nothing here" body="Try a different filter or search." />;
  return (
    <div className="space-y-3">
      {rows.map((r) => {
        const open = expanded === `q-${r.id}`;
        return (
          <div key={r.id} className={r.handled ? "opacity-65" : ""}>
            <RowShell
              testid={`quote-row-${r.id}`}
              expanded={open}
              onClick={() => onToggle(open ? null : `q-${r.id}`)}
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-display text-white text-lg leading-tight truncate">
                    {r.name}{" "}
                    <span className="text-white/40 text-sm font-sans">
                      · {r.service_tier}
                    </span>
                  </p>
                  <HandledPill row={r} />
                </div>
                <p className="text-white/50 text-xs mt-1">
                  <span className="text-[#D4AF37]">
                    ${r.estimate_low}–${r.estimate_high}
                  </span>{" "}
                  · {r.vehicle_size} · {dateFmt(r.created_at)}
                  {(r.preferred_date || r.preferred_slot) && (
                    <span className="text-white/70"> · wants {preferredFmt(r)}</span>
                  )}
                </p>
              </div>
              <ChevronDown
                className={`w-4 h-4 text-white/50 transition-transform shrink-0 ${
                  open ? "rotate-180 text-[#D4AF37]" : ""
                }`}
              />
            </RowShell>
            {open && (
              <div className="border border-t-0 border-[#D4AF37]/30 bg-[#0D0B05] p-6 grid md:grid-cols-2 gap-5">
                <Field label="Email">
                  <a href={`mailto:${r.email}`} className="link-gold break-all">
                    {r.email}
                  </a>
                </Field>
                <Field label="Phone">
                  <a href={`tel:${r.phone}`} className="link-gold">
                    {r.phone}
                  </a>
                </Field>
                <Field label="Vehicle">{r.vehicle_size}</Field>
                <Field label="Service">{r.service_tier}</Field>
                <Field label="Preferred time" full>
                  <span data-testid={`quote-preferred-${r.id}`}>
                    {preferredFmt(r)}
                  </span>
                </Field>
                <Field label="Add-ons" full>
                  {r.addons?.length ? r.addons.join(", ") : "None"}
                </Field>
                <Field label="Estimate" full>
                  <span className="text-[#D4AF37] font-display text-xl">
                    ${r.estimate_low} – ${r.estimate_high} NZD
                  </span>
                </Field>
                {r.notes && (
                  <Field label="Notes" full>
                    <p className="whitespace-pre-wrap leading-relaxed">
                      {r.notes}
                    </p>
                  </Field>
                )}
                <div className="md:col-span-2 flex flex-wrap gap-3 mt-2">
                  <a
                    href={`mailto:${r.email}?subject=Your%20${encodeURIComponent(
                      r.service_tier
                    )}%20quote`}
                    className="btn-gold px-5 py-2.5 text-[11px] flex items-center gap-2"
                  >
                    <Mail className="w-3.5 h-3.5" /> Confirm via Email
                  </a>
                  <a
                    href={`tel:${r.phone}`}
                    className="btn-outline-gold px-5 py-2.5 text-[11px] flex items-center gap-2"
                  >
                    <Phone className="w-3.5 h-3.5" /> Call
                  </a>
                  <HandledButton
                    row={r}
                    onToggleHandled={onToggleHandled}
                    updatingId={updatingId}
                    testid={`quote-handled-${r.id}`}
                  />
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">
        {label}
      </p>
      <div className="text-white text-sm mt-2">{children}</div>
    </div>
  );
}

function EmptyState({ title, body }) {
  return (
    <div className="border border-white/8 bg-[#0E0E0E] p-12 text-center">
      <p className="font-display text-white text-2xl font-light">{title}</p>
      <p className="text-white/45 text-sm mt-3 max-w-md mx-auto">{body}</p>
    </div>
  );
}
